import { NavLink, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useOffice } from '../contexts/OfficeContext'
import { ShieldLogo } from './ShieldLogo'
import { useLang } from '../contexts/LangContext'

// Labels are resolved at render time via useTranslation, so we store keys
const navItemDefs = [
  {
    to: '/overview',
    labelKey: 'nav.overview',
    icon: (
      <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
      </svg>
    ),
  },
  {
    to: '/clients',
    labelKey: 'nav.clients',
    icon: (
      <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
      </svg>
    ),
  },
  {
    to: '/cases',
    labelKey: 'nav.cases',
    icon: (
      <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" />
      </svg>
    ),
  },
  {
    to: '/alerts',
    labelKey: 'nav.alerts',
    badge: true,
    icon: (
      <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
      </svg>
    ),
  },
  {
    to: '/reports',
    labelKey: 'nav.reports',
    adminOnly: true,
    icon: (
      <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
      </svg>
    ),
  },
  {
    to: '/audit-log',
    labelKey: 'nav.auditLog',
    adminOnly: true,
    dividerBefore: true,
    icon: (
      <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
      </svg>
    ),
  },
  {
    to: '/settings',
    labelKey: 'nav.settings',
    icon: (
      <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
]

interface SidebarProps {
  officeName: string
  mobileOpen: boolean
  onClose: () => void
  collapsed: boolean
  onToggleCollapse: () => void
}

export function Sidebar({ officeName, mobileOpen, onClose, collapsed, onToggleCollapse }: SidebarProps) {
  const { user, signOut } = useAuth()
  const { unreadAlerts, userRole } = useOffice()
  const { t } = useLang()
  const navigate = useNavigate()

  const navItems = navItemDefs
    .filter(item => !('adminOnly' in item && item.adminOnly) || userRole === 'admin')
    .map((item) => ({ ...item, label: t(item.labelKey) }))

  // Avatar initials from display_name (up to two words), then email
  const displayName = (user?.user_metadata?.display_name as string | undefined) || ''
  const avatarLetter = (displayName.trim()
    ? displayName.trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('')
    : (user?.email?.[0] ?? '?')).toUpperCase()

  async function handleLogout() {
    await signOut()
    navigate('/login')
  }

  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/40 z-40 md:hidden" onClick={onClose} />
      )}

      <aside
        className={`fixed left-0 top-0 bottom-0 flex flex-col z-50 transition-all duration-200 md:translate-x-0 ${
          mobileOpen ? 'translate-x-0 w-64' : '-translate-x-full'
        } ${collapsed ? 'md:w-16' : 'md:w-64'}`}
        style={{ backgroundColor: 'var(--nav-bg)', borderRight: '1px solid var(--nav-edge)' }}
      >
        {/* Logo area */}
        <div className={`pt-5 pb-4 flex items-center ${collapsed ? 'px-3 justify-center' : 'px-5 justify-between'}`}>
          <div className={`flex items-center gap-3 ${collapsed ? 'md:justify-center' : ''}`}>
            <ShieldLogo size={collapsed ? 34 : 48} style={{ margin: collapsed ? 0 : '-6px -6px -6px -8px' }} />
            <div className={`${collapsed ? 'md:hidden' : ''}`}>
              <div className="text-white font-extrabold text-[16px] leading-tight tracking-tight">Immigrant Shield</div>
              <div className="text-[9.5px] font-bold uppercase tracking-[0.2em] leading-tight mt-0.5" style={{ color: 'var(--nav-accent)' }}>for Offices</div>
            </div>
          </div>
          <button
            onClick={onToggleCollapse}
            aria-label={t('sidebar.collapse')}
            className={`hidden md:flex items-center justify-center w-6 h-6 rounded transition-colors hover:text-white ${collapsed ? 'md:hidden' : ''}`}
            style={{ color: 'rgba(255,255,255,0.35)' }}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
            </svg>
          </button>
        </div>

        {/* Separator after logo area */}
        <div style={{ borderBottom: '1px solid rgba(255,255,255,0.08)', marginBottom: '8px' }} />

        {/* Expand button when collapsed */}
        {collapsed && (
          <button
            onClick={onToggleCollapse}
            aria-label={t('sidebar.expand')}
            className="hidden md:flex items-center justify-center mx-auto mb-2 w-6 h-6 rounded transition-colors hover:text-white"
            style={{ color: 'rgba(255,255,255,0.35)' }}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
            </svg>
          </button>
        )}

        {/* Nav */}
        <nav className={`flex-1 space-y-1 ${collapsed ? 'px-2' : 'px-3.5'}`}>
          {navItems.map((item) => (
            <div key={item.to}>
            {'dividerBefore' in item && item.dividerBefore && (
              <div className="my-3 mx-1.5" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }} />
            )}
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              onClick={onClose}
              title={collapsed ? item.label : undefined}
              className={({ isActive }) =>
                `flex items-center rounded-[11px] text-[14.5px] transition-all ${
                  collapsed
                    ? 'justify-center px-0 py-2.5'
                    : 'gap-3 px-3.5 py-[11px]'
                } ${
                  isActive
                    ? `text-white font-bold ${collapsed ? '' : 'border-l-[3px]'}`
                    : `font-medium hover:text-white ${collapsed ? '' : 'border-l-[3px] border-transparent'}`
                }`
              }
              style={({ isActive }) =>
                isActive
                  ? {
                      backgroundColor: 'var(--nav-active)',
                      borderLeftColor: collapsed ? undefined : 'var(--nav-accent)',
                    }
                  : {
                      color: 'var(--nav-text)',
                    }
              }
              onMouseEnter={(e) => {
                const el = e.currentTarget
                if (!el.classList.contains('text-white')) {
                  el.style.backgroundColor = 'rgba(255,255,255,0.05)'
                }
              }}
              onMouseLeave={(e) => {
                const el = e.currentTarget
                if (!el.classList.contains('text-white')) {
                  el.style.backgroundColor = ''
                }
              }}
            >
              <div className="relative">
                {item.icon}
                {'badge' in item && item.badge && unreadAlerts > 0 && collapsed && (
                  <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-red-500 border" style={{ borderColor: 'var(--nav-bg)' }} />
                )}
              </div>
              {!collapsed && (
                <>
                  <span className="flex-1 md:block hidden">{item.label}</span>
                  {'badge' in item && item.badge && unreadAlerts > 0 && (
                    <span className="hidden md:inline-flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold min-w-[20px] h-5 px-1.5">
                      {unreadAlerts}
                    </span>
                  )}
                </>
              )}
              {/* Mobile always shows text */}
              <span className="flex-1 md:hidden">{item.label}</span>
              {'badge' in item && item.badge && unreadAlerts > 0 && (
                <span className="md:hidden inline-flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold min-w-[20px] h-5 px-1.5">
                  {unreadAlerts}
                </span>
              )}
            </NavLink>
            </div>
          ))}
        </nav>

        {/* Footer — user info + logout */}
        <div
          className={`pb-5 pt-4 mt-auto ${collapsed ? 'px-2' : 'px-4'}`}
          style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}
        >
          {!collapsed ? (
            <div className="flex items-center gap-3">
              {/* Avatar + name, click through to Settings */}
              <div
                onClick={() => navigate('/settings')}
                title={t('nav.settings')}
                className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer rounded-lg -mx-1.5 px-1.5 py-1 transition-colors hover:bg-white/5"
              >
                <div
                  className="w-[38px] h-[38px] rounded-full flex items-center justify-center flex-shrink-0 text-[13px] font-extrabold select-none text-white"
                  style={{ backgroundColor: 'var(--nav-active)' }}
                >
                  {avatarLetter}
                </div>

                {/* Name + office */}
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-white text-sm truncate leading-tight">
                    {(user?.user_metadata?.display_name as string | undefined) || user?.email?.split('@')[0] || t('auditLog.user')}
                  </div>
                  <div className="text-xs truncate leading-tight mt-0.5" style={{ color: 'var(--nav-text-muted)' }}>{officeName}</div>
                </div>
              </div>

              {/* Logout icon button */}
              <button
                onClick={handleLogout}
                title={t('sidebar.signOut')}
                className="flex-shrink-0 p-1.5 rounded-lg transition-colors cursor-pointer text-white/30 hover:text-red-400 hover:bg-red-500/10"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
                </svg>
              </button>
            </div>
          ) : (
            <>
              {/* Collapsed: just logout icon centered */}
              <button
                onClick={handleLogout}
                title={t('sidebar.signOut')}
                className="flex items-center justify-center w-full py-2 rounded-lg transition-colors cursor-pointer text-white/30 hover:text-red-400 hover:bg-red-500/10"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
                </svg>
              </button>
            </>
          )}
        </div>
      </aside>
    </>
  )
}
